// Welcome offer popup for hesitant product-page visitors: it opens after `delay` seconds (or on desktop
// exit intent) only if the visitor has not acted (add to cart, buy now). It stays quiet for `snooze-days`
// after a dismissal and for good once the visitor has subscribed. Add ?popup=1 to any product URL to
// force it open for testing. The success state is server-rendered and opened by <s-dialog open-on-connect>.
const STORAGE_KEY = 'cooltrucs-welcome-popup';

class WelcomePopup extends HTMLElement {
  #ac = null;
  #timer = null;
  #cancelled = false;
  #shown = false;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.querySelector('[data-copy-code]')?.addEventListener('click', (event) => this.#copy(event.currentTarget), opts);

    if (this.hasAttribute('subscribed')) {
      this.#remember('subscribed');
      return;
    }

    const forced = new URLSearchParams(window.location.search).get('popup') === '1';
    const open = () => {
      if (this.#shown || this.#cancelled || dialog.open) return;
      this.#shown = true;
      this.#clearTimer();
      dialog.showModal();
    };

    if (forced) {
      // Testing link: clear any saved dismissal so normal behavior can be re-tested afterwards.
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* storage unavailable */
      }
      open();
      return;
    }

    dialog.addEventListener('close', () => this.#remember('dismissed'), opts);
    if (this.#isSnoozed()) return;
    // A visitor with items in the cart has already acted: don't interrupt them.
    if (this.hasAttribute('cart-has-items')) return;

    // Any buying action means the visitor is engaged: never interrupt them.
    const cancel = () => {
      this.#cancelled = true;
      this.#clearTimer();
    };
    document.addEventListener(
      'submit',
      (event) => {
        if (event.target instanceof HTMLFormElement && event.target.action.includes('/cart/add')) cancel();
      },
      { ...opts, capture: true }
    );
    document.addEventListener(
      'click',
      (event) => {
        if (event.target instanceof Element && event.target.closest('button[name="add"], shopify-accelerated-checkout, [data-shopify="payment-button"]')) cancel();
      },
      { ...opts, capture: true }
    );

    const delay = Number(this.getAttribute('delay') || 12) * 1000;
    this.#timer = window.setTimeout(open, delay);

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.addEventListener(
        'mouseout',
        (event) => {
          if (!event.relatedTarget && event.clientY <= 0) open();
        },
        opts
      );
    }
  }

  disconnectedCallback() {
    this.#clearTimer();
    this.#ac?.abort();
  }

  #clearTimer() {
    if (this.#timer) window.clearTimeout(this.#timer);
    this.#timer = null;
  }

  #isSnoozed() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (!saved) return false;
      if (saved.state === 'subscribed') return true;
      const days = Number(this.getAttribute('snooze-days') || 7);
      return Date.now() - saved.at < days * 86400000;
    } catch {
      return false;
    }
  }

  #remember(state) {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (saved?.state === 'subscribed') return;
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ state, at: Date.now() }));
    } catch {
      /* storage unavailable: the popup simply may show again */
    }
  }

  async #copy(button) {
    const code = button.dataset.copyCode;
    const label = button.querySelector('[data-copy-label]');
    try {
      await navigator.clipboard.writeText(code);
      if (label) label.textContent = button.dataset.copiedLabel;
    } catch {
      /* clipboard blocked: the code stays visible to copy by hand */
    }
  }
}

if (!customElements.get('s-welcome-popup')) {
  customElements.define('s-welcome-popup', WelcomePopup);
}
